import { SummitServiceTrackItem, SummitServiceData, SummitServiceResponse } from './types'
import { WebcastListItemProps } from '../../components/WebcastListItem/WebcastListItem'
import fetchJSON from '../fetchJSON'
import getCanonicalUrl from '../getCanonicalUrl'
import isIn from '../isIn'
// import { xssSanitize as sanitize } from '@bt-react/libs/sanitize'

/** Definitions of the FetchSummit Response */
export interface SummitResponse {
  title: string
  link: string
  summitDate: string
  summitEndDate?: string
  img: string
  tracks: { key: number, value: string }[]
  upcoming: WebcastListItemProps[]
  live: WebcastListItemProps[]
  onDemand: WebcastListItemProps[]
  eventCount: number
  showTracks: boolean
  target: string,
  type: string,
  isSeriesOrOriginals?: boolean
}

export interface SummitTrackSelectProps {
  tracks?: { key: number, value: string }[]
}

type GetTracksResponse = Pick<SummitResponse, 'tracks' | 'upcoming' | 'live' | 'onDemand' | 'eventCount' | 'showTracks'>

export const summitItemUTM = 'utm_source=brighttalk-portal&utm_medium=web&utm_campaign=player-page-feed&utm_content=summit-talk'

export const defaultState: SummitResponse = Object.freeze({
  eventCount: 0,
  img: '',
  link: '',
  live: [],
  onDemand: [],
  showTracks: false,
  summitDate: '',
  title: '',
  tracks: [],
  upcoming: [],
  target: '',
  type: ''
})

const isCurrentTalk = ({ communicationId }: SummitServiceTrackItem, currentWebcastId: string) => `${communicationId}` === currentWebcastId

const formatTalk = (hostUrl, talkItem: SummitServiceTrackItem, isCurrentTalkBool: boolean): WebcastListItemProps | undefined => {

  if (talkItem.status === '' || isCurrentTalkBool) return

  const { status, title } = talkItem

  const canonicalTalkUrl = getCanonicalUrl(hostUrl, `${talkItem.channelId}`, `${talkItem.communicationId}`)

  const post = {
    id: talkItem.communicationId,
    channelId: Number(talkItem.channelId),
    date: talkItem.scheduled,
    title,
    status,
    thumbnail: {
      alt: title,
      url: talkItem.thumbnail ? talkItem.thumbnail : ''
    },
    link: `${canonicalTalkUrl}?${summitItemUTM}`,
    presenter: talkItem.presenter,
    description: talkItem.description,
  }

  if (talkItem?.track) post['track'] = talkItem.track

  if (talkItem.duration) {

    post['duration'] = (typeof talkItem.duration === 'string') ? parseInt(talkItem.duration) : talkItem.duration

  }

  return post

}

function sortByDateAsc(webcastList: WebcastListItemProps[]): WebcastListItemProps[] {

  return webcastList
    .sort((a, b) => (a.date < b.date) ? -1 : ((a.date > b.date) ? 1 : 0))

}

function sortByDateDesc(webcastList: WebcastListItemProps[]): WebcastListItemProps[] {

  return webcastList
    .sort((a, b) => (a.date < b.date) ? 1 : ((a.date > b.date) ? -1 : 0))

}

function getHttpUrl(urlString: string = '') {

  if (new RegExp("^(http|https)://", "i").test(urlString)) {

    return urlString;

  } else {

    return urlString ? ('https://' + urlString) : urlString;

  }

}

const getTracks = (hostUrl = '', { tracks = [] }: SummitServiceData, webcastId: string): GetTracksResponse => {

  const _tracks: SummitResponse['tracks'] = []
  const upcoming: WebcastListItemProps[] = []
  const live: WebcastListItemProps[] = []
  const onDemand: WebcastListItemProps[] = []
  let includeWebcast = false
  let showTracks = false
  let combinedWebinars = []

  // Get the tracks and generate a Record of Talk -> Track items to lookup against later
  if (tracks.length > 0) {

    for (const track of tracks) {

      _tracks.push({
        key: track.id,
        value: track.name
      })

      for (const talk of (track.communications || [])) {

        combinedWebinars.push({ ...talk, track: track.id })

      }

    }

  }

  const filteredObj = combinedWebinars.reduce((previousValue, currentValue) => {
    previousValue[`${currentValue.channelId}${currentValue.communicationId}`] = currentValue
    return previousValue
  }, {})

  combinedWebinars = Object.values(filteredObj)

  for (const talk of combinedWebinars) {

    const isCurrentTalkBool = isCurrentTalk(talk, webcastId)
    includeWebcast = !includeWebcast ? isCurrentTalkBool : includeWebcast

    // If the talk we're looking at on the player page is upcoming or live or recorded, switch to true
    showTracks = isCurrentTalkBool ? isIn(talk?.status, 'upcoming', 'live', 'recorded') : showTracks
    const summitTalk = formatTalk(hostUrl, talk, isCurrentTalkBool)

    if (summitTalk) {

      switch (summitTalk?.status) {

        case 'recorded':
          onDemand.push(summitTalk)
          break
        case 'live':
          live.push(summitTalk)
          break
        case 'upcoming':
          upcoming.push(summitTalk)
          break
        default:
          break

      }

    }

  }

  // If the talk we're looking at on the player page isn't live or upcoming or recorded, check if any talks in the summit are
  showTracks = !showTracks
    ? (upcoming.length > 0 || live.length > 0 || onDemand.length > 0)
    : showTracks

  return {
    tracks: _tracks,
    upcoming: sortByDateAsc(upcoming),
    live: sortByDateAsc(live),
    onDemand: sortByDateDesc(onDemand),
    eventCount: upcoming.length + live.length + onDemand.length + (includeWebcast ? 1 : 0),
    showTracks
  }

}

export function formatSummitResponse(hostUrl = '', response: SummitServiceResponse, webcastId: string): SummitResponse {

  const summitData = response?.summits?.[0] || null;

  if (!summitData) return defaultState

  const tracks = getTracks(hostUrl, summitData, webcastId)
  const isSeriesOrOriginals = summitData?.type?.toLowerCase()?.trim()?.includes('series') || summitData?.type?.toLowerCase()?.trim()?.includes('originals')

  return {
    title: summitData?.title ?? defaultState.title,
    // title: sanitize(summitData?.title) ?? defaultState.title,
    summitDate: summitData?.scheduledStartDate ?? defaultState.summitDate,
    summitEndDate: summitData?.scheduledEndDate ?? defaultState.summitDate,
    img: summitData?.imageUrl ?? defaultState.img,
    link: getHttpUrl(summitData?.customUrl?.trim() || summitData?.wordpressLink?.trim()) || defaultState.link,
    target: (summitData?.type?.toLowerCase()?.trim()?.includes('virtual') || isSeriesOrOriginals) ? '_blank' : '',
    type: summitData?.type,
    isSeriesOrOriginals: isSeriesOrOriginals,
    ...tracks
  }

}

/**
 * Fetches the Summits data from Wordpress
 * @param channelId - The channel ID to check
 * @param webcastId - The webcast ID to check
 */
async function fetchSummit(channelId: string, webcastId: string, hostUrl = ''): Promise<SummitResponse> {

  if (!channelId || !webcastId) return defaultState

  const { data } = await fetchJSON<SummitServiceResponse>('fetchSummit', `${hostUrl || process.env.HOST}/service/summit/v2/summit/public/communication/summits?channelId=${channelId}&communicationId=${webcastId}`)
  return formatSummitResponse(hostUrl, data, webcastId)

}

export default fetchSummit
